import { useMsal } from '@azure/msal-react'
import { loginRequest } from '../auth/msalConfig'
import { isMobilePhone } from '../utils/device'
import './Login.css'

// Microsoft logo mark (shared)
function MsLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}

export default function Login() {
  const { instance } = useMsal()
  const handleLogin = () => instance.loginRedirect(loginRequest)

  // Phones get a dedicated layout; computers / tablets keep the original.
  const isMobile = isMobilePhone()

  /* ═══════════════ MOBILE (phones) — dedicated layout ═══════════════ */
  if (isMobile) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100vw',
        background: 'linear-gradient(170deg, #0c2f45 0%, #16384f 42%, #1e4f6b 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 22px calc(32px + env(safe-area-inset-bottom))',
        boxSizing: 'border-box',
        overflowY: 'auto'
      }}>
        {/* Logo sits on the navy background (its lettering is light, so it's
            unreadable on a white card — on navy it reads clearly) */}
        <img src="/images/logo.png" alt="FocusOne"
          style={{ width: '250px', maxWidth: '72%', height: 'auto', objectFit: 'contain', marginBottom: '26px' }} />

        <div style={{
          width: '100%',
          maxWidth: '420px',
          background: '#fff',
          borderRadius: '18px',
          padding: '32px 24px',
          boxShadow: '0 18px 50px rgba(0,0,0,0.3)',
          textAlign: 'center',
          boxSizing: 'border-box'
        }}>
          <h1 style={{
            fontSize: '25px', fontWeight: 800, color: '#0D2B3E',
            lineHeight: 1.2, margin: '0 0 12px'
          }}>
            One Platform, One Team,{' '}
            <span style={{ color: '#1A8AAF' }}>Global Success.</span>
          </h1>

          <p style={{ color: '#7298AD', fontSize: '14px', lineHeight: 1.6, margin: '0 0 28px' }}>
            Sign in with your organisation account to access the FocusOne Market Intelligence Platform.
          </p>

          <button onClick={handleLogin} style={{
            width: '100%', padding: '15px 20px',
            background: '#D63B0A', color: '#fff',
            border: 'none', borderRadius: '10px',
            fontSize: '16px', fontWeight: 700,
            cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: '10px',
            boxShadow: '0 6px 16px rgba(214,59,10,0.3)'
          }}>
            <MsLogo />
            Login with Azure AD
          </button>

          <p style={{ marginTop: '16px', color: '#aaa', fontSize: '12px' }}>
            Protected by Microsoft Entra ID · SSO enabled
          </p>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginTop: '22px' }}>
          {['Market Overview', 'Suppliers Insights', 'Portfolio Analytics'].map((t) => (
            <span key={t} style={{
              padding: '7px 14px', borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.25)',
              color: 'rgba(255,255,255,0.85)', fontSize: '12px'
            }}>{t}</span>
          ))}
        </div>
      </div>
    )
  }

  /* ═══════════════ DESKTOP / BIG SCREENS — original layout (unchanged) ═══════════════ */
  return (
    <div style={{ display: 'flex', width: '100vw', height: '100vh', overflow: 'hidden', margin: 0, padding: 0 }}>

      {/* LEFT PANEL */}
      <div style={{
        width: '42%',
        height: '100%',
        background: 'linear-gradient(135deg, #0c2f45 0%, #1a5e7d 100%)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '60px 50px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Logo */}
        <div style={{ marginBottom: '40px' }}>
          <img src="/images/logo.png" alt="FocusOne" style={{ height: "120px", width: "360px", objectFit: "contain" }} />
        </div>

        {/* Tagline */}
        <p style={{ color: '#fff', fontSize: '22px', fontWeight: '700', lineHeight: '1.4', marginBottom: '32px' }}>
          Centralize market insights, streamline in-licensing, and make data-driven portfolio decisions.
        </p>

        {/* Pills */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {['Market Overview', 'Suppliers Insights', 'Portfolio Analytics'].map(t => (
            <span key={t} style={{
              padding: '8px 16px', borderRadius: '20px',
              border: '1px solid rgba(255,255,255,0.25)',
              color: 'rgba(255,255,255,0.85)', fontSize: '13px'
            }}>{t}</span>
          ))}
        </div>

        {/* Background cross decoration */}
        <div style={{
          position: 'absolute', right: '-20px', bottom: '80px',
          width: '200px', height: '200px', opacity: 0.08,
          background: 'radial-gradient(circle, #fff 0%, transparent 70%)'
        }} />
      </div>

      {/* RIGHT PANEL */}
      <div style={{
        width: '58%',
        height: '100%',
        background: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '60px 80px'
      }}>
        <div style={{ maxWidth: '420px', width: '100%', textAlign: 'center' }}>
          <h1 style={{
            fontSize: '42px', fontWeight: '800',
            color: '#0D2B3E', lineHeight: '1.15', marginBottom: '16px'
          }}>
            One Platform,<br />One Team,{' '}
            <span style={{ color: '#1A8AAF' }}>Global Success.</span>
          </h1>

          <p style={{ color: '#7298AD', fontSize: '15px', marginBottom: '36px', lineHeight: '1.6' }}>
            Sign in with your organisation account to access the FocusOne Market Intelligence Platform.
          </p>

          <button onClick={handleLogin} style={{
            width: '100%', padding: '16px 24px',
            background: '#D63B0A', color: '#fff',
            border: 'none', borderRadius: '8px',
            fontSize: '16px', fontWeight: '700',
            cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', gap: '10px'
          }}>
            <MsLogo />
            Login with Azure AD
          </button>

          <p style={{ marginTop: '16px', color: '#aaa', fontSize: '12px' }}>
            Protected by Microsoft Entra ID · SSO enabled
          </p>
        </div>
      </div>

    </div>
  )
}
